import React, { useCallback, useMemo, useState } from 'react'
import { Col, Form, Row } from 'antd'
import { find, get, isNil, map, values } from 'lodash'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormInput,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import {
  StyledPageActions,
  StyledPageHeader,
  StyledPageTitle,
  StyledVerticalSpace,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { nodePropType } from '../../../common/prop-types'

import {
  INGREDIENT_SPECIAL_CASE_DEFINITION_DEFAULT_VALUES,
  INGREDIENT_SPECIAL_CASE_TYPE_LABELS,
  INGREDIENT_SPECIAL_CASE_TYPES,
} from '../../constants'
import { ingredientSpecialCasePropType } from '../../prop-types'
import {
  IngredientSpecialCaseDefinitionFormItem,
  IngredientSpecialIgnoredCombinationCaseDefinitionFormItem,
} from '../sections'

import { ingredientSpecialCaseFormInitialValues } from './initial-values'

const IngredientSpecialCaseEditorForm = ({
  ingredientSpecialCase,
  onSubmit,
  ingredientTreeData,
}) => {
  const [typeValue, setTypeValue] = useState(
    ingredientSpecialCase?.type || INGREDIENT_SPECIAL_CASE_TYPES.PARSING_RULE,
  )

  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.INGREDIENT_SPECIAL_CASE.UPDATE,
  ])

  const initialValues = useMemo(
    () => ingredientSpecialCaseFormInitialValues(ingredientSpecialCase),
    [ingredientSpecialCase],
  )

  const ruleTypeOptions = useMemo(
    () =>
      map(values(INGREDIENT_SPECIAL_CASE_TYPES), value => ({
        value,
        label: get(INGREDIENT_SPECIAL_CASE_TYPE_LABELS, value),
      })),
    [],
  )

  const isRuleTypeEditable = useMemo(
    () => isNil(ingredientSpecialCase?.id),
    [ingredientSpecialCase?.id],
  )

  const handleFieldsChange = useCallback(
    changedValues => {
      const changedType = find(
        changedValues,
        ({ name }) => name[0] === 'type',
      )?.value

      if (!isNil(changedType) && changedType !== typeValue) {
        form.setFieldsValue({
          caseDefinition:
            INGREDIENT_SPECIAL_CASE_DEFINITION_DEFAULT_VALUES[changedType],
        })

        setTypeValue(changedType)
      }

      setToDirty(true)
    },
    [form, setToDirty, typeValue],
  )

  return (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editIngredientSpecialCase"
      onFinish={onSubmit}
      autoComplete="off"
      onFieldsChange={handleFieldsChange}
    >
      <StyledPageHeader sticky>
        <StyledPageTitle>
          {ingredientSpecialCase
            ? 'Edit Ingredient Special Case'
            : 'Create Ingredient Special Case'}
        </StyledPageTitle>
        <StyledPageActions>
          <FormButtons
            permissions={[
              PERMISSIONS.INGREDIENT_SPECIAL_CASE.CREATE,
              PERMISSIONS.INGREDIENT_SPECIAL_CASE.UPDATE,
            ]}
            isDisabled={!isDirty}
            onReset={resetForm}
          />
        </StyledPageActions>
      </StyledPageHeader>
      <Row gutter={[16]}>
        <StyledVerticalSpace>
          <Col span={5}>
            <FormInput
              disabled={!isFormEnabled}
              name="name"
              label="Name"
              labelAbove
              placeholder="Input name..."
              rules={[
                {
                  required: true,
                },
              ]}
            />
          </Col>
          <Col span={6}>
            <FormSelect
              name="type"
              labelAbove
              label="Rule type"
              showSearch={false}
              allowClear={false}
              options={ruleTypeOptions}
              placeholder="Select rule type..."
              disabled={isRuleTypeEditable ? !isFormEnabled : true}
              rules={[
                {
                  required: true,
                },
              ]}
            />
          </Col>
          <Col span={12}>
            <FormTextareaInput
              disabled={!isFormEnabled}
              name="description"
              label="Description"
              labelAbove
              placeholder="Input description..."
              autoSize
            />
          </Col>

          <Col span={12}>
            {typeValue === INGREDIENT_SPECIAL_CASE_TYPES.IGNORED_COMBINATION ? (
              <IngredientSpecialIgnoredCombinationCaseDefinitionFormItem
                disabled={!isFormEnabled}
                ingredientTreeData={ingredientTreeData}
              />
            ) : (
              <IngredientSpecialCaseDefinitionFormItem
                disabled={!isFormEnabled}
                name="caseDefinition"
                label="Case Definition"
                labelAbove
                id={ingredientSpecialCase?.id}
                form={form}
              />
            )}
          </Col>
        </StyledVerticalSpace>
      </Row>
    </DirtyPromptForm>
  )
}

IngredientSpecialCaseEditorForm.propTypes = {
  ingredientTreeData: PropTypes.arrayOf(nodePropType).isRequired,
  ingredientSpecialCase: ingredientSpecialCasePropType,
  onSubmit: PropTypes.func.isRequired,
}

IngredientSpecialCaseEditorForm.defaultProps = {
  ingredientSpecialCase: undefined,
}

export default IngredientSpecialCaseEditorForm
